import { ENoviDashboardSider } from '@/pages/Novi/enums/ENoviDashboardSider';
import { AccountState, User, UserAccount } from '@/types/TAccounts';
import { PayloadAction, Slice, createSlice } from '@reduxjs/toolkit';
import posthog from 'posthog-js';
import { ReceptionistDashboardConfigureTabs } from 'src/enums/EReceptionistDashboardConfigureTabs';
import { ReceptionistDashboardSiderItem } from 'src/enums/EReceptionistDashboardSiderItem';
import { UserDashboardSiderItem } from 'src/enums/EUserDashboardSiderItem';
import { cloneDeep } from 'lodash';

const initialState: AccountState = {
	isAuthenticated: false,
	authToken: undefined,
	userAccount: {
		id: '',
		username: '',
		emailAddress: '',
		customerId: '',
		inboundDemo: false,
		outboundDemo: false,
		userType: 'Client',
	},
	agents: [],
	user: null, // this is basically selected agent now
	selectedAgentId: null,
	currentPage: ReceptionistDashboardSiderItem.DASHBOARD,
	activeConfigureTab: '',
	agentPreview: false,
	crmGroups: null,
};

export const slice: Slice<
	AccountState,
	{
		cleanState: () => void;
		setAccount: (state: AccountState, action: PayloadAction<AccountState>) => void;
		updateUserAccount: (
			state: AccountState,
			action: PayloadAction<{ userAccount: Partial<UserAccount> }>,
		) => void;
		setAgents: (state: AccountState, action: PayloadAction<{ agents: User[] }>) => void;
		setSelectedAgentId: (
			state: AccountState,
			action: PayloadAction<{ selectedAgentId: string | null }>,
		) => void;
		setSelectedAgent: (state: AccountState, action: PayloadAction<{ user: User | null }>) => void;

		setInstructionSteps: (
			state: AccountState,
			action: PayloadAction<{ instructionSteps: number[] }>,
		) => void;
		setTestingNumber: (
			state: AccountState,
			action: PayloadAction<{ testing_number: string }>,
		) => void;
		setUserContacts: (
			state: AccountState,
			action: PayloadAction<{ userContacts: { [key: string]: string } }>,
		) => void;
		setUserTimeZone: (state: AccountState, action: PayloadAction<{ timeZone: string }>) => void;
		updateSelectedAgent: (
			state: AccountState,
			action: PayloadAction<{ user: Partial<User> }>,
		) => void;
		updateProvisionedNumber: (
			state: AccountState,
			action: PayloadAction<{ provisionedNumber: string }>,
		) => void;

		setCurrentPage: (
			state: AccountState,
			action: PayloadAction<
				ReceptionistDashboardSiderItem | ENoviDashboardSider | UserDashboardSiderItem
			>,
		) => void;
		setActiveConfigureTab: (
			state: AccountState,
			action: PayloadAction<ReceptionistDashboardConfigureTabs>,
		) => void;
		setAgentPreview: (
			state: AccountState,
			action: PayloadAction<{ agentPreview: boolean }>,
		) => void;
		setCRMGroups: (state: AccountState, action: PayloadAction<any>) => void;
	},
	'account'
> = createSlice({
	name: 'account',
	initialState,
	reducers: {
		cleanState: () => initialState,
		setAccount: (state: AccountState, action: PayloadAction<AccountState>) => {
			state.authToken = action.payload.authToken;
			state.userAccount = {
				...action.payload.userAccount,
				resellerClient: action.payload.userAccount.userType === 'SubAccount',
			};
			state.isAuthenticated = action.payload.isAuthenticated;

			if (action.payload.selectedAgentId) {
				state.selectedAgentId = action.payload.selectedAgentId;
			}

			if (action.payload.user) {
				state.user = action.payload.user;
			}
			// Identify user with PostHog if authenticated
			if (state.isAuthenticated && state.userAccount.id) {
				posthog.identify(state.userAccount.username, {
					...state.userAccount,
					createdAt: new Date(state.userAccount?.createdAt || '2025-01-01T20:11:42.779Z').getTime(),
				});

				const username = state.userAccount.username;
				const deepCopy = cloneDeep(state.userAccount);

				window.clarity('identify', username, null, null, username);
				Object.entries(deepCopy).forEach(([key, value]) => {
					if (value !== undefined && value !== null) {
						window.clarity('set', key, value);
					}
				});
			}
		},
		setSelectedAgent: (state: AccountState, action: PayloadAction<{ user: User | null }>) => {
			if (action.payload.user === null) {
				state.user = null;
				return;
			}

			// Create a new user object with all properties
			const updatedUser = {
				...action.payload.user,
				// Set recordTransferredCall if call_recording_enabled is true and recordTransferredCall is not set
				recordTransferredCall:
					action.payload.user?.call_recording_enabled === true &&
					(action.payload.user?.recordTransferredCall === undefined ||
						action.payload.user?.recordTransferredCall === null)
						? true
						: action.payload.user?.recordTransferredCall,
			};

			// Update the state with the new user object
			state.user = updatedUser as User;
		},

		updateUserAccount: (
			state: AccountState,
			action: PayloadAction<{ userAccount: Partial<UserAccount> }>,
		) => {
			state.userAccount = { ...state.userAccount, ...action.payload.userAccount };
		},
		setAgents: (state: AccountState, action: PayloadAction<{ agents: User[] }>) => {
			state.agents = action.payload.agents;
		},
		setSelectedAgentId: (
			state: AccountState,
			action: PayloadAction<{ selectedAgentId: string | null }>,
		) => {
			state.selectedAgentId = action.payload.selectedAgentId;
		},

		setInstructionSteps: (
			state: AccountState,
			action: PayloadAction<{ instructionSteps: number[] }>,
		) => {
			state.user!.instruction_steps = action.payload.instructionSteps;
		},
		setTestingNumber: (state: AccountState, action: PayloadAction<{ testing_number: string }>) => {
			state.user!.testing_number = action.payload.testing_number;
		},
		setUserContacts(
			state: AccountState,
			action: PayloadAction<{ userContacts: { [key: string]: string } }>,
		) {
			state.user!.contacts = action.payload.userContacts;
		},
		setUserTimeZone(state: AccountState, action: PayloadAction<{ timeZone: string }>) {
			state.user!.time_zone = action.payload.timeZone;
		},
		updateSelectedAgent(state: AccountState, action: PayloadAction<{ user: Partial<User> }>) {
			if (state.user) {
				state.user = { ...state.user, ...action.payload.user };
			}
		},
		setCurrentPage: (
			state: AccountState,
			action: PayloadAction<
				ReceptionistDashboardSiderItem | ENoviDashboardSider | UserDashboardSiderItem
			>,
		) => {
			state.currentPage = action.payload;
		},
		setActiveConfigureTab: (
			state: AccountState,
			action: PayloadAction<ReceptionistDashboardConfigureTabs>,
		) => {
			state.activeConfigureTab = action.payload;
		},
		updateProvisionedNumber(state, action) {
			if (state.selectedAgentId) {
				state.user!.provisionedNumber = action.payload.provisionedNumber;
			}
		},
		setAgentPreview: (state: AccountState, action: PayloadAction<{ agentPreview: boolean }>) => {
			state.agentPreview = action.payload.agentPreview;
		},
		setCRMGroups: (state: AccountState, action: PayloadAction<any>) => {
			state.crmGroups = action.payload;
		},
	},
});

export const {
	cleanState,
	setAccount,
	setInstructionSteps,
	setTestingNumber,
	setUserContacts,
	setUserTimeZone,
	updateSelectedAgent,
	setCurrentPage,
	setActiveConfigureTab,
	updateProvisionedNumber,
	setAgents,
	setSelectedAgentId,
	setSelectedAgent,
	updateUserAccount,
	setAgentPreview,
	setCRMGroups,
} = slice.actions;

export default slice.reducer;
